/**
 * QuickAddModal — residence journal edition.
 *
 * Shared modal shell used across every "Add / Start / Update" flow in
 * ARA Home OS. Warm ivory surface, hairline separators, Fraunces titles,
 * Inter body, tracked-caption eyebrow, underline inputs, text-link primary
 * action. No rounded coloured buttons, no drop shadows, no gradients.
 *
 * Props identical to the original — every callsite migrates transparently.
 */
import { useState, useEffect } from "react";
import { X as XIcon } from "lucide-react";

export default function QuickAddModal({
  open,
  onClose,
  title = "Add",
  eyebrow = "Quick add",
  testid = "quick-add-modal",
  fields = [],
  submitLabel = "Save",
  // `accent` retained for API compatibility with earlier callsites; ignored.
  accent: _accent,
  onSubmit,
}) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(fields.map((f) => [f.id, f.initial ?? ""]))
  );

  useEffect(() => {
    if (open) {
      setValues(Object.fromEntries(fields.map((f) => [f.id, f.initial ?? ""])));
    }
  }, [open, fields]);

  if (!open) return null;

  const handleChange = (id, val) => setValues((v) => ({ ...v, [id]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const missing = fields.find((f) => f.required && !String(values[f.id] || "").trim());
    if (missing) return;
    const keepOpen = onSubmit?.(values);
    if (!keepOpen) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 rise-ara"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testid}-title`}
      data-testid={testid}
    >
      {/* Ivory-wash backdrop, no black overlay */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(28, 26, 22, 0.28)', backdropFilter: 'blur(2px)' }}
        onClick={onClose}
        data-testid={`${testid}-backdrop`}
      />

      {/* Ivory sheet, hairline border, no shadow, no gradient */}
      <div
        className="relative w-full max-w-lg p-8 sm:p-10 max-h-[90vh] overflow-y-auto rise-ara rise-ara-s1"
        style={{
          background: 'var(--ara-surface)',
          border: '1px solid var(--ara-line)',
          borderRadius: 4,
        }}
      >
        {/* Header — tracked eyebrow + Fraunces title */}
        <div className="flex items-start justify-between gap-6 mb-8">
          <div className="min-w-0">
            <p className="caption-ara" data-testid={`${testid}-eyebrow`}>{eyebrow}</p>
            <h3
              id={`${testid}-title`}
              className="mt-3 font-display leading-tight tracking-[-0.01em]"
              style={{ fontSize: 26, fontWeight: 400, color: 'var(--ara-ink)' }}
            >
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid={`${testid}-close-btn`}
            className="shrink-0 w-8 h-8 flex items-center justify-center transition-ara hover:opacity-70"
            style={{ color: 'var(--ara-ink-muted)' }}
          >
            <XIcon className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <hr className="rule-ara mb-8" />

        <form onSubmit={handleSubmit} className="space-y-7">
          {fields.map((f) => {
            const inputBase =
              "w-full bg-transparent outline-none py-2.5 text-[17px] tracking-[-0.01em]";
            const inputStyle = {
              color: 'var(--ara-ink)',
              borderBottom: '1px solid var(--ara-line)',
              transition: 'border-color 220ms cubic-bezier(0.22, 1, 0.36, 1)',
            };
            const focusOn = (e) => { e.currentTarget.style.borderBottom = '1px solid var(--ara-ink)'; };
            const focusOff = (e) => { e.currentTarget.style.borderBottom = '1px solid var(--ara-line)'; };

            return (
              <label key={f.id} className="block">
                <span className="caption-ara">{f.label}</span>

                {f.type === 'textarea' ? (
                  <textarea
                    value={values[f.id] ?? ""}
                    onChange={(e) => handleChange(f.id, e.target.value)}
                    rows={f.rows || 3}
                    placeholder={f.placeholder}
                    required={f.required}
                    autoFocus={f.autoFocus}
                    onFocus={focusOn}
                    onBlur={focusOff}
                    data-testid={`${testid}-${f.id}`}
                    className={`${inputBase} mt-2 resize-none`}
                    style={inputStyle}
                  />
                ) : f.type === 'select' ? (
                  <select
                    value={values[f.id] ?? ""}
                    onChange={(e) => handleChange(f.id, e.target.value)}
                    required={f.required}
                    autoFocus={f.autoFocus}
                    onFocus={focusOn}
                    onBlur={focusOff}
                    data-testid={`${testid}-${f.id}`}
                    className={`${inputBase} mt-2 appearance-none pr-6`}
                    style={{
                      ...inputStyle,
                      backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%236B6B66' stroke-width='1.25' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right center',
                      backgroundSize: '10px 6px',
                    }}
                  >
                    {(f.options || []).filter(Boolean).map((o) => {
                      const val = typeof o === 'string' ? o : (o && o.value);
                      const lbl = typeof o === 'string' ? o : (o && (o.label ?? o.value));
                      return (
                        <option key={val} value={val}>{lbl}</option>
                      );
                    })}
                  </select>
                ) : (
                  <input
                    type={f.type || 'text'}
                    value={values[f.id] ?? ""}
                    onChange={(e) => handleChange(f.id, e.target.value)}
                    placeholder={f.placeholder}
                    required={f.required}
                    autoFocus={f.autoFocus}
                    onFocus={focusOn}
                    onBlur={focusOff}
                    data-testid={`${testid}-${f.id}`}
                    className={`${inputBase} mt-2`}
                    style={inputStyle}
                  />
                )}
                {f.hint && (
                  <span className="caption-ara mt-2 block" style={{ color: 'var(--ara-ink-soft)' }}>
                    {f.hint}
                  </span>
                )}
              </label>
            );
          })}

          <hr className="rule-ara-soft mt-2" />

          <div className="flex items-center justify-between gap-6 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid={`${testid}-cancel`}
              className="link-ara"
              style={{
                fontSize: 13,
                letterSpacing: '0.06em',
                color: 'var(--ara-ink-muted)',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid={`${testid}-save`}
              className="link-ara inline-flex items-center gap-2"
              style={{ fontSize: 14, letterSpacing: '0.06em' }}
            >
              {submitLabel} &nbsp;→
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
